import { Container, Divider } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Grid } from "@mui/material";
import {
  ButtonGroupTop,
  CustomTable,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SearchTextInput,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { Fragment, useCallback, useEffect, useState } from "react";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import {
  listDspSFTPConfigs,
  deleteDspSFTPConfig,
} from "utils/DSPSFTPService";
import CreateOrEditDspSftpModal from "./CreateOrEditDspSftpModal";

function DspSftpConfiguration() {
  const classes = globalStyles();

  const [loadingPage, setLoadingPage] = useState(true);
  const [dataTable, setDataTable] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    per_page: 10,
    search: "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await listDspSFTPConfigs({
        page: queryParams.page,
        per_page: queryParams.per_page,
        size: queryParams.per_page,
        search: queryParams.search,
      });

      const responseData = res?.data?.data;
      const meta = res?.data?.meta;

      let list = Array.isArray(responseData) ? responseData : [];
      // Filter locally if search query is present and API didn't filter
      if (queryParams.search) {
        const s = queryParams.search.toLowerCase();
        list = list.filter(
          item =>
            item?.display_name?.toLowerCase().includes(s) ||
            item?.dsp_slug?.toLowerCase().includes(s) ||
            item?.name?.toLowerCase().includes(s) ||
            item?.slug?.toLowerCase().includes(s) ||
            item?.host?.toLowerCase().includes(s) ||
            item?.action?.toLowerCase().includes(s) ||
            item?.auth_type?.toLowerCase().includes(s)
        );
      }

      if (meta?.total) {
        const pageCount = getTotalPage(meta?.total, queryParams?.per_page);
        setTableTotalPage(pageCount || 1);
        setDataTable(list);
      } else {
        // If no server pagination metadata, handle client-side pagination
        const total = list.length;
        const pageCount = getTotalPage(total, queryParams?.per_page);
        setTableTotalPage(pageCount || 1);
        const startIndex = (queryParams.page - 1) * queryParams.per_page;
        const pagedList = list.slice(startIndex, startIndex + queryParams.per_page);
        setDataTable(list.length > queryParams.per_page ? pagedList : list);
      }
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  const handleDelete = async item => {
    try {
      const id = item?.id || item?.dsp_id;
      await deleteDspSFTPConfig(id);
      ModalSuccess("Configuration has been deleted").then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  const handleOpenCreate = () => {
    setSelectedItem(null);
    setModalOpen(true);
  };

  const handleOpenEdit = item => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  const handleModalSaved = () => {
    handleModalClose();
    getDataTable();
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Fragment>
      <Page className={classes.root} title="DSP SFTP Configuration">
        <Container maxWidth={false}>
          <HeaderTitle title="Configuration" breadcrumbData={breadcrumbData} />
          <ButtonGroupTop />
          <Divider className={classes?.flatDivider} />
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            my="16px"
          >
            <Grid item>
              <SearchTextInput
                placeholder="Search"
                value={queryParams?.search}
                onChange={e =>
                  handleChangeQueryParams(e?.target?.value, "search")
                }
                disabled={loadingPage}
              />
            </Grid>
            <Grid item>
              <PrimaryButton
                label="Create Config"
                startIcon={<Add />}
                onClick={handleOpenCreate}
                size="large"
                disabled={loadingPage}
              />
            </Grid>
          </Grid>
          <CustomTable
            isLoading={loadingPage}
            columnTable={columnTable}
            data={dataTable}
            page={queryParams?.page}
            pageSize={queryParams?.per_page}
            pageCount={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangePageSize={e =>
              handleChangeQueryParams(e?.target?.value, "per_page")
            }
            isHaveAction
            handleEdit={handleOpenEdit}
            handleDelete={handleDelete}
            deleteConfirmation
            deleteConfirmationKey="display_name"
          />
        </Container>
      </Page>

      {modalOpen && (
        <CreateOrEditDspSftpModal
          open={modalOpen}
          editItem={selectedItem}
          onClose={handleModalClose}
          onSaved={handleModalSaved}
        />
      )}
    </Fragment>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Configuration",
    link: "/admin/konfigurasi/dsp-sftp",
  },
  {
    label: "DSP SFTP Configuration",
    active: true,
  },
];

const formatAuthType = type => {
  if (!type) return "-";
  if (type === "password" || type === "direct_password") return "Direct Password";
  if (type === "key_bastion" || type === "bastion" || type === "bastion_host") return "Bastion Host";
  if (type === "key") return "SSH Key";
  return type;
};

const columnTable = [
  {
    name: "display_name",
    title: "DSP",
  },
  {
    name: "dsp_slug",
    title: "DSP Slug Name",
  },
  {
    name: "action",
    title: "Action Mode",
    renderText: val => {
      if (!val) return "-";
      return val.charAt(0).toUpperCase() + val.slice(1);
    },
  },
  {
    name: "auth_type",
    title: "Auth Type",
    renderText: val => formatAuthType(val),
  },
  {
    name: "all",
    title: "Host / Port",
    renderText: row => (row?.host ? `${row.host}:${row.port || 22}` : "-"),
  },
];

export default DspSftpConfiguration;
